import cx from 'classnames';
import React, { useId } from 'react';
import { Controller } from 'react-hook-form';

import { Button, Input, Popup, PopupSection, Text, Textarea } from '@flixbus/honeycomb-react';

import CrewMemberSearch from './CrewMemberSearch';
import { MODAL_SET_ON_HOLD_DOWN_TEXT } from './text';
import { useModalSetOnHoldDown } from './useModalSetOnHoldDown';

import styles from './styles.module.css';

const ModalSetOnHoldDown = ({ initialData, employeeId, baseTimezone, onClose }) => {
	const startDateId = useId();
	const startTimeId = useId();
	const tillDateId = useId();
	const tillTimeId = useId();
	const noteId = useId();

	const {
		control,
		miDay,
		minEndTime,
		maxEndTime,
		isSubmitDisabled,
		isStartTimeDisabled,
		isEndDisabled,
		isEndTimeDisabled,
		driverName,
		isValidDate,
		validateYear,
		validateEndTime,
		setDriverName,
		setOriginalCrewMemberId,
		handleStartDateChange,
		handleStartTimeChange,
		handleEndDateChange,
		handleEndTimeChange,
		handleSubmit,
		handleClose,
	} = useModalSetOnHoldDown({ initialData, employeeId, baseTimezone, onClose });

	return (
		<Popup active id="modal-hold-down" extraClasses={styles.modalWrapper}>
			<PopupSection type="title">{MODAL_SET_ON_HOLD_DOWN_TEXT.setOnHoldDownTitle}</PopupSection>
			<PopupSection type="content" extraClasses={styles.scrollableContent}>
				<Text extraClasses={styles.descriptionMargin}>
					{MODAL_SET_ON_HOLD_DOWN_TEXT.setOnHoldDownDescription}
				</Text>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}
				>
					<div className={styles.timeRow}>
						<div className={styles.timeWrapper}>
							<Controller
								name="from"
								control={control}
								rules={{ required: true, validate: { year: validateYear, range: isValidDate } }}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={startDateId}
										type="date"
										label={MODAL_SET_ON_HOLD_DOWN_TEXT.startDateAndTime}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.dateInput}
										onChange={(e) => handleStartDateChange(field, e)}
									/>
								)}
							/>
							<Controller
								name="fromTime"
								control={control}
								rules={{ required: true }}
								disabled={isStartTimeDisabled}
								render={({ field: { ref: _ref, ...field } }) => (
									<Input
										{...field}
										id={startTimeId}
										type="time"
										aria-label={MODAL_SET_ON_HOLD_DOWN_TEXT.startTimeAriaLabel}
										extraClasses={styles.timeInput}
										onChange={(e) => handleStartTimeChange(field, e)}
									/>
								)}
							/>
						</div>

						<div className={styles.timeWrapper}>
							<Controller
								name="till"
								control={control}
								rules={{ required: true, validate: { year: validateYear, range: isValidDate } }}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={tillDateId}
										type="date"
										min={miDay}
										disabled={isEndDisabled}
										label={MODAL_SET_ON_HOLD_DOWN_TEXT.endDateAndTime}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.dateInput}
										onChange={(e) => handleEndDateChange(field, e)}
									/>
								)}
							/>
							<Controller
								name="tillTime"
								control={control}
								rules={{ required: true, validate: validateEndTime }}
								disabled={isEndTimeDisabled}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={tillTimeId}
										type="time"
										aria-label={MODAL_SET_ON_HOLD_DOWN_TEXT.endTimeAriaLabel}
										placeholder={MODAL_SET_ON_HOLD_DOWN_TEXT.endTimePlaceholder}
										min={minEndTime}
										max={maxEndTime}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.timeInput}
										onChange={(e) => handleEndTimeChange(field, e)}
									/>
								)}
							/>
						</div>
					</div>
					<div className={cx(styles.row, styles.full)}>
						<CrewMemberSearch
							driverName={driverName}
							setDriverName={setDriverName}
							setValue={setOriginalCrewMemberId}
						/>
					</div>
					<div className={cx(styles.row, styles.full)}>
						<Controller
							name="note"
							control={control}
							rules={{ required: false }}
							render={({ field: { ref: _ref, ...field } }) => (
								<Textarea
									{...field}
									id={noteId}
									label={MODAL_SET_ON_HOLD_DOWN_TEXT.noteLabel}
									placeholder={MODAL_SET_ON_HOLD_DOWN_TEXT.notePlaceholder}
									onChange={(e) => field.onChange(e.target.value)}
								/>
							)}
						/>
					</div>
				</form>
			</PopupSection>
			<PopupSection type="actions" extraClasses={styles.actionsSection}>
				<Button onClick={handleClose}>{MODAL_SET_ON_HOLD_DOWN_TEXT.cancelBtn}</Button>
				<Button
					type="submit"
					appearance="primary"
					disabled={isSubmitDisabled}
					onClick={handleSubmit}
				>
					{MODAL_SET_ON_HOLD_DOWN_TEXT.confirmBtn}
				</Button>
			</PopupSection>
		</Popup>
	);
};

export default ModalSetOnHoldDown;
